import React, { useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';

import styled from 'styled-components';

import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

import { createShouldForwardProp } from 'helpers/shouldForwardProp';

// hooks
import useAnalytics from '../../hooks/helper/useAnalytics';
import { useAuthSession } from '../../hooks/useAuthSession';

import API from '../../services/api';

import ToggleInput from '../inputs/toggleInput';
import SupportModal from '../modals/supportModal';
// components
import AampeTooltip from '../shared/AampeTooltip/AampeTooltip';

const shouldForwardProps = createShouldForwardProp(['collapsed']);
interface SideNavFooterProps {
    collapsed: boolean;
}
interface StyledSideNavFooterProps {
    collapsed: boolean;
}

const SideNavFooter: React.FC<React.PropsWithChildren<SideNavFooterProps>> = ({
    collapsed,
}) => {
    const { user: currentUser, updateSession } = useAuthSession();
    const { track } = useAnalytics();

    const isBetaUser = currentUser?.isBetaUser;
    const isBetaMode = currentUser?.isBetaMode;

    const [displaySupportModal, setDisplaySupportModal] =
        useState<boolean>(false);

    const toggleBetaMode = () => {
        updateSession((prev) => {
            if (!prev || !prev.user) return prev;
            return {
                ...prev,
                user: {
                    ...prev.user,
                    is_beta_user: !prev.user.is_beta_user,
                },
            };
        });
    };

    const handleLogout = async () => {
        window.analytics.reset();
        Sentry.setUser(null);

        await API.logout();
    };

    const handleSupportClick = () => {
        track('support_button_clicked');
        setDisplaySupportModal(true);
    };

    const userInfo = useMemo(
        () => ({
            name: currentUser?.name ?? currentUser?.email ?? '',
            email: currentUser?.email ?? '',
            customer: currentUser?.customerName ?? '',
            role: currentUser?.role ?? '',
        }),
        [currentUser],
    );

    const renderAvatar = useMemo(
        () => (
            <Avatar collapsed={collapsed}>
                <AvatarText collapsed={collapsed}>
                    {userInfo.name[0]}
                </AvatarText>
            </Avatar>
        ),
        [userInfo, collapsed],
    );

    if (!currentUser) return <></>;

    return (
        <>
            <SideNavFooterContainer collapsed={collapsed}>
                {collapsed ? (
                    <FooterWrapper>
                        <SupportLinks collapsed={collapsed}>
                            <AampeTooltip
                                title="Raise a ticket"
                                placement="right"
                            >
                                <button
                                    type="button"
                                    onClick={handleSupportClick}
                                    aria-label="Raise a ticket"
                                >
                                    <ICONS.ticket width={16} height={16} />
                                </button>
                            </AampeTooltip>
                            <AampeTooltip
                                title="Read documentation"
                                placement="right"
                            >
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href="https://kb.aampe.com/"
                                    aria-label="Read documentation"
                                >
                                    <ICONS.helpCircle size={16} />
                                </a>
                            </AampeTooltip>
                            <AampeTooltip title="Inspiration" placement="right">
                                <a
                                    rel="noreferrer"
                                    target="_blank"
                                    href="https://kb.aampe.com/tools"
                                    aria-label="Inspiration"
                                >
                                    <ICONS.openBook width={16} height={16} />
                                </a>
                            </AampeTooltip>
                            {isBetaUser && (
                                <AampeTooltip
                                    title={`Beta Mode ${
                                        isBetaMode ? 'enabled' : 'disabled'
                                    }`}
                                    placement="right"
                                >
                                    <BetaMode>
                                        <ToggleInput
                                            isActive={isBetaMode}
                                            toggleIsActive={toggleBetaMode}
                                            isLoading={false}
                                            isError={false}
                                            disabled={false}
                                            width={16}
                                        />
                                    </BetaMode>
                                </AampeTooltip>
                            )}
                        </SupportLinks>
                        <AampeTooltip title={userInfo.name}>
                            {renderAvatar}
                        </AampeTooltip>
                    </FooterWrapper>
                ) : (
                    <FooterWrapper>
                        <SupportLinks collapsed={collapsed}>
                            <button type="button" onClick={handleSupportClick}>
                                <ICONS.ticket width={16} height={16} />
                                Raise a ticket
                            </button>
                            <a
                                href="https://kb.aampe.com/"
                                rel="noreferrer"
                                target="_blank"
                            >
                                <ICONS.helpCircle size={16} />
                                Read documentation
                            </a>
                            <a
                                href="https://kb.aampe.com/tools"
                                rel="noreferrer"
                                target="_blank"
                            >
                                <ICONS.openBook width={16} height={16} />
                                Inspiration
                            </a>
                            {isBetaUser && (
                                <BetaMode>
                                    Beta mode{' '}
                                    {isBetaMode ? 'enabled' : 'disabled'}
                                    <ToggleInput
                                        isActive={isBetaMode}
                                        toggleIsActive={toggleBetaMode}
                                        isLoading={false}
                                        isError={false}
                                        disabled={false}
                                        width={16}
                                    />
                                </BetaMode>
                            )}
                        </SupportLinks>
                        <ProfileWrapper>
                            {renderAvatar}
                            <SideNavFooterNameContainer>
                                <SideNavFooterName>
                                    {userInfo.name}
                                </SideNavFooterName>
                                <SideNavFooterEmail>
                                    {userInfo.customer} | {userInfo.role}
                                </SideNavFooterEmail>
                            </SideNavFooterNameContainer>
                            <AampeTooltip title="Logout">
                                <LogoutButton
                                    onClick={handleLogout}
                                    type="button"
                                    aria-label="Logout"
                                >
                                    <ICONS.logout width={14} height={14} />
                                </LogoutButton>
                            </AampeTooltip>
                        </ProfileWrapper>
                    </FooterWrapper>
                )}
            </SideNavFooterContainer>
            <SupportModal
                isOpen={displaySupportModal}
                handleClose={() => setDisplaySupportModal(false)}
            />
        </>
    );
};

const SupportLinks = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledSideNavFooterProps>(
    (props: StyledSideNavFooterProps) => `
    display: flex;
    flex-direction: column;
    gap: ${aampeTheme.spacing(1)};
    margin-bottom: ${aampeTheme.spacing(1)};
    width: ${props.collapsed ? 'max-content' : '100%'};
    justify-items: center;

    & > * {
        display: flex;
        gap: ${aampeTheme.spacing(0.5)};
        align-items: center;
        color: ${aampeTheme.colors.textAccessibleWG01};
        font-size: ${aampeTheme.size.font.textSmall};
        line-height: 120%;
        border: 0;
        background: transparent;
        padding: ${aampeTheme.spacing4(1, 0)};
        cursor: pointer;
        transition: color 200ms ease-in-out;

         &:hover {
        color: ${aampeTheme.colors.textAccessibleWG01};
         ${
             props.collapsed
                 ? `
                svg {
                    color: ${aampeTheme.colors.textAccessibleWG01};
                }
            `
                 : ''
         }
    }
    }
    svg {
        transition: color 200ms ease-in-out;
    }
    button:hover,
    a:hover {
        color: ${aampeTheme.colors.textAccessibleWG01};
    }

    & a:any-link {
        color: ${aampeTheme.colors.textAccessibleWG01};
        text-decoration: none;
    }
`,
);
const LogoutButton = styled.button({
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    color: aampeTheme.colors.textAccessibleWG01,
    transition: 'color 200ms ease-in-out',

    '&:hover': {
        color: aampeTheme.colors.error,
    },
});
const SideNavFooterContainer = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledSideNavFooterProps>((props: StyledSideNavFooterProps) => ({
    display: 'flex',
    justifyContent: props.collapsed ? 'center' : 'space-between',
    alignItems: 'center',
    cursor: props.collapsed ? 'pointer' : 'default',
}));

const SideNavFooterNameContainer = styled.span(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginLeft: aampeTheme.spacing4(2),
}));

const SideNavFooterName = styled.span(() => ({
    color: 'rgba(13, 13, 13, 1)',
    fontSize: '12px',
    fontWeight: 'bold',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '125px',
}));

const SideNavFooterEmail = styled.span(() => ({
    color: aampeTheme.colors.textAccessibleWG01,
    marginTop: aampeTheme.spacing(0.5),
    fontSize: aampeTheme.size.font.textExtraSmall,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '125px',
}));

const Avatar = styled.div.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledSideNavFooterProps>(() => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '32px',
    height: '32px',
    backgroundColor: aampeTheme.colors.solidWG01,
    borderRadius: '4px',
}));

const AvatarText = styled.span.withConfig({
    shouldForwardProp: shouldForwardProps,
})<StyledSideNavFooterProps>((props: StyledSideNavFooterProps) => ({
    color: aampeTheme.colors.white,
    fontSize: props.collapsed ? 22 : 18,
}));

const BetaMode = styled.div({
    marginTop: aampeTheme.spacing(0.5),
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: '#4c5b69',
    justifyContent: 'space-between',
    '&:hover': {
        color: `${aampeTheme.colors.textAccessibleWG01} !important`, // Override parent hover with !important
    },
});

const ProfileWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: aampeTheme.spacing(1),
    borderRadius: '8px',
    border: '1px solid #E2E2E2',
});

const FooterWrapper = styled.div({
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
    alignItems: 'center',
});

export default SideNavFooter;
